import { useState } from "react";
import { Button, cx } from "../../ui";
import { useAllowMeet } from "./MeetButton";
import { meetEnabled, meetErrorText, type Account } from "./model";

type BadgeAccount = Pick<Account, "id" | "workspaceId" | "provider" | "address" | "scopes">;

/**
 * "Meet: enabled / not enabled" for one account, with the consent action when it is not. Meant
 * for Settings → Accounts (one per Google account row); renders nothing for Outlook accounts.
 */
export function MeetScopeBadge({ account }: { account: BadgeAccount }) {
  const allowMeet = useAllowMeet();
  return <MeetScopeBadgeView account={account} onAllow={allowMeet} />;
}

export function MeetScopeBadgeView({ account, onAllow }: { account: BadgeAccount; onAllow: (account: BadgeAccount) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (account.provider !== "gmail") return null;
  const on = meetEnabled(account);
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-xs">
      <span
        className={cx("rounded-full border px-2 py-0.5 font-medium", on ? "border-accent text-accent" : "border-border text-muted")}
        data-meet={on ? "enabled" : "not-enabled"}
      >
        {on ? "Meet: enabled" : "Meet: not enabled"}
      </span>
      {on ? null : (
        <Button
          variant="ghost"
          className="min-h-7 px-2 text-xs"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            onAllow(account).catch((e: unknown) => {
              setBusy(false);
              setError(meetErrorText(e));
            });
          }}
        >
          {`Allow Meet for ${account.address}`}
        </Button>
      )}
      {error === null ? null : (
        <span role="alert" className="text-danger">
          {error}
        </span>
      )}
    </span>
  );
}
